import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import COLORS from '../../../constants/colors';

interface NewGroupDialogProps {
  open: boolean;
  creating: boolean;
  onCancel: () => void;
  onCreate: (name: string, key: string) => void;
}

/** The key is made from the name: "Sukuk Funds" → sukuk_funds. */
const toKey = (name: string) =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

/** A new group starts switched on, with no products and no rules (open to everyone until set up). */
export function NewGroupDialog({ open, creating, onCancel, onCreate }: NewGroupDialogProps) {
  const [name, setName] = useState('');
  const key = toKey(name);
  const valid = /^[a-z][a-z0-9_]*$/.test(key);

  useEffect(() => {
    if (open) {
      setName('');
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={creating ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>New product group</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Group name"
          placeholder="e.g. Sukuk Funds"
          value={name}
          onChange={(event) => setName(event.target.value)}
          fullWidth
          inputProps={{ maxLength: 255 }}
          helperText={key ? `Key: ${key}` : 'English letters and numbers'}
          error={Boolean(name) && !valid}
        />
        <Typography sx={{ fontSize: '0.8125rem', color: COLORS.LIGHT_GRAY, mt: '8px' }}>
          It starts with no products and no rules. Add products and rules, then publish.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={creating}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!valid || creating} onClick={() => onCreate(name.trim(), key)}>
          {creating ? 'Creating…' : 'Create group'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
